import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { ImageDetailsPageTabs } from 'app/plugins/openstack/components/images/details/model'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DeleteDialog from 'core/components/DeleteDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge/Badge'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { humanReadableSize } from 'openstack/helpers'
import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { formatDate } from 'utils/misc'
import { listNonPaginatedVolumes } from '../volumes/actions'
import { nonPaginatedVolumesByIdSelector } from '../volumes/selectors'
import { IVolumeDetailsPageTabs } from '../volumes/volume-details/model'
import { deleteVolumeSnapshot, listVolumeSnapshots } from './actions'
import { IVolumeSnapshotDetailsPageTabs } from './details/model'
import { volumeSnapshotsSelector } from './selectors'
import { getVolumeSnapshotColumns } from 'app/plugins/openstack/components/storage/snapshots/helper'

type ModelDataKey = DataKeys.OpenstackVolumeSnapshots
type SelectorModel = ArrayElement<ReturnType<typeof volumeSnapshotsSelector>>
type ActionParams = InferActionParams<typeof listVolumeSnapshots>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackVolumeSnapshots',
  listTablePrefs,
)

const searchTargets = ['name', 'id', 'description', 'status', 'volume_id']

const getBadgeVariant = (status) => {
  return (
    {
      creating: 'warning',
      available: 'success',
      'backing-up': 'warning',
      deleting: 'warning',
      error: 'error',
      deleted: 'error',
      unmanaging: 'warning',
      restoring: 'warning',
      error_deleting: 'warning',
      'in-use': 'success',
    }[status] || 'unknown'
  )
}

const getBadgeText = (status) => {
  const text =
    {
      creating: 'Creating',
      available: 'Available',
      'backing-up': 'Backing Up',
      deleting: 'Deleting',
      error: 'Error',
      deleted: 'Deleted',
      unmanaging: 'Unmanaging',
      restoring: 'Restoring',
      error_deleting: 'Error Deleting',
      'in-use': 'In Use',
    }[status] || status
  return text
}

export const StatusCellComponent = ({ value, item }) => {
  const classes = useStyles()
  return (
    <div className={classes.statusCell}>
      <Badge variant={getBadgeVariant(value)} text={getBadgeText(value)} />
    </div>
  )
}

export default function VolumeSnapshotsListPage() {
  const { history } = useReactRouter()
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload, refreshing: volumeSnapshotsRefreshing } = useListAction(
    listVolumeSnapshots,
  )
  const data = useAppSelector(volumeSnapshotsSelector)

  const { loading: loadingVolumes } = useListAction(listNonPaginatedVolumes)
  const volumesById = useSelector(nonPaginatedVolumesByIdSelector)

  const [selectedVolumeSnapshot, setSelectedVolumeSnapshot] = useState(null)
  const [showDeleteVolumeSnapshotDialog, setShowDeleteVolumeSnapshotDialog] = useState(null)

  const redirectToCreateVolume = (selectedVolumeSnapshotId) => {
    history.push(
      `${routeManager.routes.openstack.createVolume.path()}?snapshotId=${selectedVolumeSnapshotId}`,
    )
  }

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (volumes) => {
        return volumes?.length === 1
      },
      disabledTooltip: 'Select a single volume snapshot to create a volume from',
      icon: 'plus',
      tooltip: 'Create New Volume',
      label: 'Create New Volume',
      handleAction: (volumes) => {
        redirectToCreateVolume(volumes[0]?.id)
      },
    },
  ]

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Create New Volume',
      icon: 'plus',
      handleClick: (volumeSnapshot) => {
        redirectToCreateVolume(volumeSnapshot?.id)
      },
      refreshAfterSuccess: false,
      hideIfDisabled: false,
    },
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (volumeSnapshot) => {
        setSelectedVolumeSnapshot([volumeSnapshot])
        setShowDeleteVolumeSnapshotDialog(true)
      },
      cond: (selected) => {
        return !selected?.attachedVmSnapshot
      },
      disabledTooltip:
        'The selected volume snapshot cannot be deleted because it is linked to a VM snapshot.',
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
  ]

  const columns = useMemo(() => getVolumeSnapshotColumns({ volumesById }), [volumesById])

  const searchFunctions = useMemo(
    () => [
      {
        property: 'volume_id',
        searchFn: (value, searchString) => {
          const volumeName = volumesById?.[value]?.name || value
          return volumeName.includes(searchString)
        },
      },
    ],
    [volumesById],
  )

  const DeleteVolumeSnapshotDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deleteVolumeSnapshot}
        title="Delete Volume Snapshot(s)"
        resourceLabel="Volume Snapshots"
      />
    ),
    [],
  )

  return (
    <>
      <DocumentMeta title="Volume Snapshots" />
      <PollingData hidden loading={loading} onReload={reload} refreshDuration={1000 * 30} />
      {showDeleteVolumeSnapshotDialog && (
        <DeleteVolumeSnapshotDialog
          rows={selectedVolumeSnapshot}
          onClose={() => setShowDeleteVolumeSnapshotDialog(false)}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OpenstackVolumeSnapshots}
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        refreshing={volumeSnapshotsRefreshing}
        data={data}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        batchActions={batchActions}
        deleteAction={deleteVolumeSnapshot}
        DeleteDialogComponent={DeleteVolumeSnapshotDialog}
        deleteCond={(volumeSnapshots: any) =>
          volumeSnapshots.every((volumeSnapshot) => !volumeSnapshot?.attachedVmSnapshot)
        }
        deleteDisabledInfo={
          'The selected volume snapshot(s) cannot be deleted because one or more of them is linked to a VM snapshot.'
        }
        rowMenuItems={rowMenuItems}
        multiSelection
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  statusCell: {
    textAlign: 'center',
  },
}))
